import { useState, useEffect, useCallback } from 'react';
import { useSupabaseClient, useUser } from '@supabase/auth-helpers-react';

export const useMenu = () => {
  const supabase = useSupabaseClient();
  const user = useUser();
  const [menuStructure, setMenuStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMenuData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the user's role from their profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role_id, is_superadmin')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.role_id && !profile?.is_superadmin) {
        setMenuStructure([]);
        return;
      }

      let accessibleMenuItems;
      
      if (profile.is_superadmin) {
        // Superadmins get access to all menu items
        const { data, error: menuError } = await supabase
          .from('menu_items')
          .select(`
            id,
            menu_group_id,
            name,
            display_order,
            path,
            icon,
            tooltip,
            menu_groups (
              id,
              name,
              display_order,
              icon,
              tooltip
            )
          `);
        
        if (menuError) throw menuError;
        accessibleMenuItems = data.map(item => ({ menu_items: item }));
      } else {
        // Regular users get access based on role_menu_items
        const { data, error: menuError } = await supabase
          .from('role_menu_items')
          .select(`
            menu_item_id,
            menu_items (
              id,
              menu_group_id,
              name,
              display_order,
              path,
              icon,
              tooltip,
              menu_groups (
                id,
                name,
                display_order,
                icon,
                tooltip
              )
            )
          `)
          .eq('role_id', profile.role_id);

        if (menuError) throw menuError;
        accessibleMenuItems = data;
      }

      // Transform the data into a hierarchical structure
      const groupedItems = accessibleMenuItems.reduce((groups, item) => {
        const menuItem = item.menu_items;
        if (!menuItem) return groups;

        const group = menuItem.menu_groups;
        if (!group) return groups;

        const existingGroup = groups.find(g => g.id === group.id);
        if (existingGroup) {
          existingGroup.items.push(menuItem);
        } else {
          groups.push({
            id: group.id,
            name: group.name,
            displayOrder: group.display_order,
            icon: group.icon,
            tooltip: group.tooltip,
            items: [menuItem]
          });
        }
        return groups;
      }, []);

      // Sort groups by display_order
      groupedItems.sort((a, b) => a.displayOrder - b.displayOrder);

      // Sort items within each group by display_order
      groupedItems.forEach(group => {
        group.items.sort((a, b) => a.display_order - b.display_order);
      });

      setMenuStructure(groupedItems);
    } catch (error) {
      console.error('Error fetching menu data:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [supabase, user]);

  useEffect(() => {
    if (user) {
      fetchMenuData();

      // Create a single channel for all table changes
      const channel = supabase
        .channel('menu_structure_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'menu_items'
          },
          (payload) => {
            console.log('Menu items changed:', payload);
            fetchMenuData();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'menu_groups'
          },
          (payload) => {
            console.log('Menu groups changed:', payload);
            fetchMenuData();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'role_menu_items'
          },
          (payload) => {
            console.log('Role menu items changed:', payload);
            fetchMenuData();
          }
        )
        .subscribe();

      // Cleanup subscription
      return () => {
        channel.unsubscribe();
      };
    }
  }, [user, fetchMenuData]);

  return { menuStructure, loading, error, refetch: fetchMenuData };
};
